import { AppRoutes } from "../models";
import { Job } from "../types";
import { DateUtils } from "./dateUtils";

const EMPLOYMENT_TYPES = new Map(Object.entries({
  fulltime: "FULL_TIME",
  parttime: "PART_TIME",
  contractor: "CONTRACTOR",
  contract: "CONTRACTOR",
  temporary: "TEMPORARY",
  temp: "TEMPORARY",
  intern: "INTERN",
  internship: "INTERN",
  volunteer: "VOLUNTEER",
  perdiem: "PER_DIEM",
  other: "OTHER",
}));

const SALARY_PERIODS = new Map(Object.entries({
  hour: "HOUR", hourly: "HOUR", perhour: "HOUR",
  day: "DAY", daily: "DAY", perday: "DAY",
  week: "WEEK", weekly: "WEEK", perweek: "WEEK",
  month: "MONTH", monthly: "MONTH", permonth: "MONTH",
  year: "YEAR", yearly: "YEAR", peryear: "YEAR",
  annual: "YEAR", annually: "YEAR", perannum: "YEAR",
}));

function normalize(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/[\s_-]/g, "");
}

function isHttpUrl(value: string | undefined): boolean {
  try {
    const url = new URL(value ?? "");
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function isJobExpired(job: Job, now = Date.now()): boolean {
  const expiry = DateUtils.toJobIsoDate(job.expiry_date);
  return expiry !== null && Date.parse(expiry) <= now;
}

export function isFullyRemoteJob(job: Job): boolean {
  // A mention of remote work in free text can describe a hybrid or even a non-remote role.
  return job.remote === true && job.hybrid !== true;
}

export function getJobPostingUrl(job: Job, origin: string): string | null {
  if (!job.jobsrmine_url?.trim()) return null;
  const url = new URL(`/${AppRoutes.JOB}/${job.jobsrmine_url.trim().replace(/^\/+/, "")}`, origin);
  url.search = "";
  url.hash = "";
  return url.href;
}

function getBaseSalary(job: Job): Record<string, unknown> | null {
  const currency = job.salary_currency?.trim().toUpperCase();
  const unitText = SALARY_PERIODS.get(normalize(job.salary_period));
  const min = Number(job.salary_minimum ?? 0);
  const max = Number(job.salary_maximum ?? 0);
  // Never infer currency from the visitor or job country, or guess a pay period.
  if (!/^[A-Z]{3}$/.test(currency ?? "") || !unitText ||
      !Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < 0 ||
      (min === 0 && max === 0) || (min > 0 && max > 0 && min > max)) {
    return null;
  }

  return {
    "@type": "MonetaryAmount",
    currency,
    value: {
      "@type": "QuantitativeValue",
      ...(min > 0 && min === max
        ? { value: min }
        : { ...(min > 0 ? { minValue: min } : {}), ...(max > 0 ? { maxValue: max } : {}) }),
      unitText,
    },
  };
}

interface JobPostingOptions {
  now?: number;
  url?: string | null;
}

/** Map only supported feed facts. Incomplete pages remain eligible for ordinary web search. */
export function getJobPostingJsonLd(
  job: Job | null,
  { now = Date.now(), url }: JobPostingOptions = {},
): Record<string, unknown> | null {
  if (!job || isJobExpired(job, now)) return null;
  const datePosted = DateUtils.toJobIsoDate(job.post_date);
  const validThrough = DateUtils.toJobIsoDate(job.expiry_date);
  const addressCountry = job.country_code?.trim().toUpperCase();
  if (!job.title?.trim() || !job.description?.replace(/<[^>]*>/g, "").trim() ||
      !job.advertiser_name?.trim() || !datePosted || Date.parse(datePosted) > now ||
      !/^[A-Z]{2}$/.test(addressCountry ?? "") ||
      // Don't advertise an unknown expiry when a supplied date cannot be interpreted.
      (job.expiry_date?.trim() && job.expiry_date.trim() !== "0" && !validThrough) ||
      !isHttpUrl(job.application_url)) {
    return null;
  }

  const employmentType = EMPLOYMENT_TYPES.get(normalize(job.employment_type));
  const baseSalary = getBaseSalary(job);
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    ...(url ? { url } : {}),
    title: job.title,
    description: job.description,
    datePosted,
    ...(validThrough ? { validThrough } : {}),
    hiringOrganization: {
      "@type": "Organization",
      name: job.advertiser_name,
      ...(isHttpUrl(job.logo_url) ? { logo: job.logo_url } : {}),
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry,
        ...(job.city?.trim() ? { addressLocality: job.city } : {}),
        ...(job.region?.trim() ? { addressRegion: job.region } : {}),
        ...(job.postal_code?.trim() ? { postalCode: job.postal_code } : {}),
      },
    },
    ...(employmentType ? { employmentType } : {}),
    ...(job.sender_reference?.trim() ? {
      identifier: { "@type": "PropertyValue", name: job.advertiser_name, value: job.sender_reference },
    } : {}),
    ...(baseSalary ? { baseSalary } : {}),
    ...(isFullyRemoteJob(job) ? { jobLocationType: "TELECOMMUTE" } : {}),
    // country is the job location, not evidence of applicant residency restrictions.
    // Add applicantLocationRequirements only once the feed supplies those restrictions.
  };
}

export function serializeJobPosting(jsonLd: Record<string, unknown>): string {
  // JSON Unicode escapes survive both HTML parsing and JSON.parse, including comments and CDATA.
  return JSON.stringify(jsonLd).replace(/</g, "\\u003c");
}
